// The v_timeline union (spec 02 §6), built in TS for FakeRest/demo mode. SQL twin: public.v_timeline.
import { demoLinked } from "./dialer/contactCall"; // GAVION demo-contact
export type TimelineCategory =
  | "calls"
  | "notes"
  | "emails"
  | "meetings"
  | "tasks";
export const TIMELINE_CATEGORIES: readonly TimelineCategory[] = Object.freeze([
  "calls",
  "notes",
  "emails",
  "meetings",
  "tasks",
]);

export type TimelineItem = {
  id: string;
  source_id: number;
  company_id: number;
  at: string;
  kind: string;
  category: TimelineCategory;
  title: string | null;
  body_snippet: string | null;
  sales_id: number | null;
  meta: Record<string, unknown>;
};

type Id = number | string;
export type TimelineSources = {
  companyNotes: {
    id: Id;
    company_id: Id;
    text: string | null;
    date: string;
    sales_id: Id | null;
    source?: string | null;
  }[];
  contactNotes: {
    id: Id;
    contact_id: Id;
    text: string | null;
    date: string | null;
    sales_id: Id | null;
  }[];
  dealNotes: {
    id: Id;
    deal_id: Id;
    text: string | null;
    date: string | null;
    sales_id: Id | null;
  }[];
  calls: {
    id: Id;
    company_id: Id | null;
    contact_id?: Id | null; // GAVION contact-call
    demo?: unknown; // GAVION demo-contact: demo.contact_linked
    brief?: unknown; // GAVION call-brief-pipeline: calls.brief (84_)
    off_crm?: boolean; // GAVION off-CRM call: calls.off_crm (86_)
    sales_id: Id | null;
    dialed_at: string;
    outcome_at: string | null;
    outcome: string | null;
    note: string | null;
    direction: string;
    duration_s: number | null;
    billsec_s: number | null;
    recording_path: string | null;
  }[];
  tasks: {
    id: Id;
    company_id: Id | null;
    contact_id: Id | null;
    deal_id: Id | null;
    type: string | null;
    text: string | null;
    due_date: string | null;
    done_date: string | null;
    sales_id: Id | null;
  }[];
  contacts: {
    id: Id;
    company_id: Id | null;
    first_name: string | null;
    last_name: string | null;
  }[];
  deals: { id: Id; company_id: Id | null; name: string }[];
  // GAVION batch 6: matched Granola notes (spec 07 §5).
  meetingNotes?: {
    id: Id;
    company_id: Id | null;
    deal_id: Id | null;
    title: string | null;
    started_at: string | null;
    synced_at: string;
    summary_markdown: string | null;
    owner_sales_id: Id;
    match_status: string;
    matched_by: string | null;
    provider: string;
    uid?: string | null;
    call_id?: Id | null; // GAVION granola-calls
  }[];
  // GAVION batch 3: emails (SQL twin: v_timeline email branch, 29_gavion_timeline.sql)
  emailMessages?: {
    id: Id;
    company_id: Id | null;
    sales_id: Id | null;
    direction: string;
    provider: string;
    status: string;
    subject: string | null;
    snippet: string | null;
    sent_at: string | null;
    received_at: string | null;
    created_at: string;
    internet_message_id: string | null;
    is_reply?: boolean | null;
    from_email: string | null;
    to_emails: string[] | null;
    // GAVION open-pixel
    open_count?: number | null;
    last_opened_at?: string | null;
    likely_auto_open?: boolean | null;
    // GAVION outlook-parity
    origin?: string | null;
    unsubscribe_request?: boolean | null;
    private_hold?: boolean | null;
    // GAVION full-email: a stored copy the caller can read (SQL twin: v_timeline 97 has_content)
    has_content?: boolean | null;
  }[];
};

const snippet = (text: string | null | undefined): string | null =>
  text == null ? null : text.slice(0, 280);
const num = (v: Id | null | undefined): number | null =>
  v === null || v === undefined ? null : Number(v);

export function buildTimeline(src: TimelineSources): TimelineItem[] {
  const contactById = new Map(src.contacts.map((c) => [String(c.id), c]));
  const dealById = new Map(src.deals.map((d) => [String(d.id), d]));
  // GAVION granola-calls: a call's matched Granola notes ride on the call (SQL: v_timeline call meta meeting_note_ids);
  // such a note is not repeated as a meeting item, unless its call is not in the timeline (no lead). Final-review I3:
  // both sides compare leads, so a call moved to another lead after the match leaves the note on its own lead.
  const callCompany = new Map(
    src.calls
      .filter((c) => c.company_id != null)
      .map((c) => [String(c.id), String(c.company_id)]),
  );
  const notesByCall = new Map<string, number[]>();
  for (const m of src.meetingNotes ?? []) {
    if (m.match_status !== "matched" || m.call_id == null) continue;
    const k = String(m.call_id);
    // SQL: gn.company_id = ca.company_id
    if (m.company_id == null || callCompany.get(k) !== String(m.company_id))
      continue;
    notesByCall.set(k, [...(notesByCall.get(k) ?? []), Number(m.id)]);
  }
  const items: TimelineItem[] = [];
  const push = (
    item: Omit<TimelineItem, "company_id"> & {
      company_id: Id | null | undefined;
    },
  ) => {
    if (item.company_id === null || item.company_id === undefined || !item.at)
      return;
    items.push({ ...item, company_id: Number(item.company_id) });
  };
  for (const n of src.companyNotes)
    push({
      id: `company_note:${n.id}`,
      source_id: Number(n.id),
      company_id: n.company_id,
      at: n.date,
      kind: "company_note",
      category: "notes",
      title: null,
      body_snippet: snippet(n.text),
      sales_id: num(n.sales_id),
      meta: { source: n.source ?? null },
    });
  for (const n of src.contactNotes) {
    const c = contactById.get(String(n.contact_id));
    push({
      id: `contact_note:${n.id}`,
      source_id: Number(n.id),
      company_id: c?.company_id,
      at: n.date ?? "",
      kind: "contact_note",
      category: "notes",
      title: c ? [c.first_name, c.last_name].filter(Boolean).join(" ") : null,
      body_snippet: snippet(n.text),
      sales_id: num(n.sales_id),
      meta: { contact_id: num(n.contact_id) },
    });
  }
  for (const n of src.dealNotes) {
    const d = dealById.get(String(n.deal_id));
    push({
      id: `deal_note:${n.id}`,
      source_id: Number(n.id),
      company_id: d?.company_id,
      at: n.date ?? "",
      kind: "deal_note",
      category: "notes",
      title: d?.name ?? null,
      body_snippet: snippet(n.text),
      sales_id: num(n.sales_id),
      meta: { deal_id: num(n.deal_id) },
    });
  }
  for (const c of src.calls) {
    // GAVION contact-call: the contact called (SQL: left join contacts, nullif(concat_ws(...), ''))
    const k =
      c.contact_id == null ? undefined : contactById.get(String(c.contact_id));
    const contactName = k
      ? [k.first_name, k.last_name]
          .map((x) => (x ?? "").trim())
          .filter(Boolean)
          .join(" ") || null
      : null;
    push({
      id: `call:${c.id}`,
      source_id: Number(c.id),
      company_id: c.company_id,
      at: c.outcome_at ?? c.dialed_at,
      kind: "call",
      category: "calls",
      title: c.outcome,
      body_snippet: snippet(c.note),
      sales_id: num(c.sales_id),
      meta: {
        outcome: c.outcome,
        direction: c.direction,
        duration_s: c.duration_s,
        billsec_s: c.billsec_s,
        has_recording: c.recording_path !== null,
        contact_id: num(c.contact_id),
        contact_name: contactName,
        // GAVION demo-contact (SQL twin: v_timeline 68_): the demo's attendee on a main-line call, not a call to him
        demo_linked: demoLinked(c),
        // GAVION granola-calls (SQL twin: v_timeline call meta)
        meeting_note_ids: [...(notesByCall.get(String(c.id)) ?? [])].sort(
          (a, b) => a - b,
        ),
        // GAVION call-brief-pipeline (SQL twin: v_timeline call meta, 84_): the brief shown before the call
        brief: c.brief ?? null,
        // GAVION off-CRM call (SQL twin: v_timeline call meta, 86_): logged after a call made from a cell
        off_crm: c.off_crm === true,
      },
    });
  }
  for (const t of src.tasks) {
    if (!t.done_date) continue;
    const companyId =
      t.company_id ??
      dealById.get(String(t.deal_id))?.company_id ??
      contactById.get(String(t.contact_id))?.company_id;
    push({
      id: `task:${t.id}`,
      source_id: Number(t.id),
      company_id: companyId,
      at: t.done_date,
      kind: "task",
      category: "tasks",
      title: t.type,
      body_snippet: snippet(t.text),
      sales_id: num(t.sales_id),
      meta: { due_date: t.due_date },
    });
  }
  // GAVION batch 6: matched Granola notes. SQL twin: the meeting_notes branch of public.v_timeline.
  for (const m of src.meetingNotes ?? []) {
    if (m.match_status !== "matched") continue;
    const companyId =
      m.company_id ?? dealById.get(String(m.deal_id))?.company_id;
    // GAVION granola-calls: shown under its call when the call is on the same lead (SQL twin: the meeting branch's
    // call filter, nk.company_id = coalesce(mn.company_id, md.company_id))
    if (
      m.call_id != null &&
      companyId != null &&
      callCompany.get(String(m.call_id)) === String(companyId)
    )
      continue;
    push({
      id: `meeting_note:${m.id}`,
      source_id: Number(m.id),
      company_id: companyId,
      at: m.started_at ?? m.synced_at,
      kind: "meeting_note",
      category: "meetings",
      title: m.title,
      body_snippet: snippet(m.summary_markdown),
      sales_id: num(m.owner_sales_id),
      meta: {
        deal_id: num(m.deal_id),
        provider: m.provider,
        matched_by: m.matched_by,
        note_uid: m.uid ?? null,
      },
    });
  }
  // GAVION batch 3: emails. Queued and cancelled rows never reach the timeline.
  for (const m of src.emailMessages ?? []) {
    if (m.status === "queued" || m.status === "cancelled") continue;
    // GAVION outlook-parity: a Private reply kept only for its pending stop request (SQL twin: v_timeline)
    if (m.private_hold) continue;
    push({
      id: `email:${m.id}`,
      source_id: Number(m.id),
      company_id: m.company_id,
      at: m.sent_at ?? m.received_at ?? m.created_at,
      kind: m.direction === "inbound" ? "email_in" : "email_out",
      category: "emails",
      title: m.subject,
      body_snippet: snippet(m.snippet),
      sales_id: num(m.sales_id),
      meta: {
        direction: m.direction,
        provider: m.provider,
        status: m.status,
        is_reply: !!m.is_reply,
        from_email: m.from_email,
        internet_message_id: m.internet_message_id,
        // GAVION open-pixel (SQL twin: v_timeline email meta)
        open_count: Number(m.open_count ?? 0),
        last_opened_at: m.last_opened_at ?? null,
        likely_auto_open: !!m.likely_auto_open,
        // GAVION outlook-parity (SQL twin: v_timeline email meta)
        origin: m.origin ?? "crm",
        unsubscribe_request: !!m.unsubscribe_request,
        // GAVION full-email (SQL twin: v_timeline email meta)
        has_content: !!m.has_content,
      },
    });
  }
  return items.sort(compareTimelineDesc);
}

// GAVION batch 3: emails
/**
 * One entry per internet message (inbox + sent copy, or two reps cc'd), at the first copy's place. It opens from the
 * current user's own copy when they have one (gavion-graph-proxy: only the mailbox owner or an admin reads a message),
 * else the first copy; it reads as the sender's copy when a CRM user sent it (bug 2026-10-26: Olivier's email with
 * Antoine in copy showed « Courriel reçu … par Antoine Gagnon »).
 * GAVION full-email: a copy whose stored body the user can read is opened first.
 */
export function dedupeEmailItems(
  items: readonly TimelineItem[],
  preferSalesId?: number | null,
): TimelineItem[] {
  const keyOf = (i: TimelineItem): string | null => {
    const raw = i.category === "emails" ? i.meta.internet_message_id : null;
    return typeof raw === "string" && raw !== "" ? raw.toLowerCase() : null;
  };
  const opened = new Map<string, TimelineItem>();
  const sent = new Map<string, TimelineItem>();
  const openScore = (i: TimelineItem): number =>
    (i.meta.has_content === true ? 2 : 0) +
    (preferSalesId != null && i.sales_id === preferSalesId ? 1 : 0);
  for (const i of items) {
    const k = keyOf(i);
    if (k === null) continue;
    const cur = opened.get(k);
    // GAVION full-email: a copy with a stored body the user can read (has_content) wins, then the user's own copy
    if (!cur || openScore(i) > openScore(cur)) opened.set(k, i);
    if (i.kind === "email_out" && i.sales_id !== null && !sent.has(k))
      sent.set(k, i);
  }
  const out: TimelineItem[] = [];
  const done = new Set<string>();
  for (const i of items) {
    const k = keyOf(i);
    if (k === null) {
      out.push(i);
      continue;
    }
    if (done.has(k)) continue;
    done.add(k);
    const open = opened.get(k)!;
    const shown = sent.get(k) ?? open;
    out.push(
      shown === open
        ? open
        : { ...shown, id: open.id, source_id: open.source_id },
    );
  }
  return out;
}

const byteOrder = (a: string, b: string): number =>
  a < b ? -1 : a > b ? 1 : 0;

/**
 * v_timeline order: at DESC, then the numeric part of the id DESC (source_id), then the id text DESC
 * compared byte-wise (SQL: `order by at desc, source_id desc, id collate "C" desc`).
 */
export function compareTimelineDesc(a: TimelineItem, b: TimelineItem): number {
  if (a.at !== b.at) return byteOrder(b.at, a.at);
  return b.source_id - a.source_id || byteOrder(b.id, a.id);
}
